"use client";
import GoogleButton from "@/components/GoogleButton";
import cx from "classnames";
import { delay } from "lodash";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";

import { useApp, useAppStore } from "@/store/app";
import { AiOutlineBug, AiOutlineHome, AiOutlineSetting } from "react-icons/ai";
import { BiHelpCircle } from "react-icons/bi";

import ShareApp from "@/components/ShareApp";
import { ServerContext } from "@/context/Server";
import useInfo from "@/utils/info";
import { useContext } from "react";
import { FaSkull } from "react-icons/fa";

const LOG_TAG = "UserDrawer";

export default function UserDrawer({
  toggleUserDrawer,
}: {
  toggleUserDrawer: () => void;
}) {
  const theme = useAppStore(({ theme }) => theme);
  const { session } = useContext(ServerContext);
  const user = { session };
  /* @ts-ignore */
  const email = user?.email;
  const { isLayoutPhone } = useInfo();
  const { signIn, signOut } = useApp();
  const router = useRouter();
  const pathName = usePathname() || "/";
  //console.debug(LOG_TAG, { user, session });

  const ListItemLink = (props: any) => {
    return (
      <div
        onClick={() => {
          console.debug(
            "UserDrawer ListItemLink click pathName",
            pathName,
            "vs",
            props.href,
          );
          if (pathName === props.href) {
            toggleUserDrawer();
            return;
          }
          toggleUserDrawer();
          router.push(props.href);
        }}
        className={cx(
          "p-4 flex items-center cursor-pointer",
          props.selected ? "bg-[#ffffff29]" : "hover:bg-[#ffffff14]",
        )}
        {...props}
      />
    );
  };

  return (
    <div className="w-[330px] bg-base-100 text-accent-content h-full">
      <div className={cx("bg-primary px-4", user ? "pt-8 pb-3" : "pt-9 pb-7")}>
        <div className="flex justify-between items-center">
          <div>
            <h6 className="text-2xl text-primary-content font-normal">
              Songbook
              <span className="font-light text-primary-content">.Studio</span>
            </h6>
            <div className="text-sm font-light text-primary-content">
              {email}
            </div>
          </div>
          <div>
            <ShareApp />
          </div>
        </div>
      </div>

      <div className="py-2">
        {isLayoutPhone && (
          <ListItemLink href="/welcome" selected={pathName === "/welcome"}>
            <div className="mr-8 flex items-center">
              <AiOutlineHome className="text-xl" />
            </div>
            <div className="text-accent-content text-base ml-0.5">Welcome</div>
          </ListItemLink>
        )}

        <ListItemLink href="/" selected={pathName === "/"}>
          <div className="mr-8 flex items-center">
            <Image
              className="ml-0.5"
              unoptimized
              src={theme === "dark" ? "/icon-white-48.png" : "/icon-48.png"}
              alt=""
              width={20}
              height={20}
            />
          </div>

          <div className="text-accent-content text-base ml-0.5">Songbook</div>
        </ListItemLink>
        <ListItemLink href="/settings" selected={pathName === "/settings"}>
          <div className="mr-8 flex items-center">
            <AiOutlineSetting className="text-xl" />
          </div>
          <div className="text-accent-content text-base ml-0.5">Settings</div>
        </ListItemLink>

        <ListItemLink href="/privacy" selected={pathName === "/privacy"}>
          <div className="mr-8 flex items-center">
            <BiHelpCircle className="text-xl" />
          </div>
          <div className="text-accent-content text-base ml-0.5">Privacy</div>
        </ListItemLink>

        <div
          className="p-4 flex items-center cursor-pointer hover:bg-[#ffffff14]"
          onClick={() => {
            window.analyticsLogEvent("Clicked Report a Bug");
            toggleUserDrawer();
            window.location.href =
              "mailto:elsigh@gmail.com?subject=SongbookStudio Bug Report";
            delay(() => {
              throw new Error("Bug Report");
            }, 100);
          }}
        >
          <div className="mr-8 flex items-center">
            <AiOutlineBug className="text-xl" />
          </div>
          <div className="text-accent-content text-base ml-0.5">
            Report a bug
          </div>
        </div>
      </div>
      <div className="divider my-0" />
      <div className="py-2">
        {session ? (
          <div
            className="p-4 flex items-center cursor-pointer hover:bg-[#ffffff14]"
            onClick={() => {
              toggleUserDrawer();
              signOut();
            }}
          >
            <div className="mr-8 flex items-center">
              <FaSkull />
            </div>
            <div className="text-accent-content text-base ml-0.5">Sign Out</div>
          </div>
        ) : (
          <div
            className="m-4 flex items-center cursor-pointer"
            onClick={() => {
              toggleUserDrawer();
              signIn();
            }}
          >
            <GoogleButton onClick={signIn} label="Sign in" />
          </div>
        )}
      </div>
    </div>
  );
}
